import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Upload, X, Save, Eye, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { donorService } from './donorService';

interface DonorFormData {
  order: number;
  image?: File;
}

const DonorForm: React.FC<{ isEdit?: boolean }> = ({ isEdit = false }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [existingImage, setExistingImage] = useState<string>('');

  const {
    register,
    handleSubmit,
    reset,
    setValue
  } = useForm<DonorFormData>({
    defaultValues: {
      order: 1
    }
  });

  // Fetch existing data for edit
  useEffect(() => {
    if (isEdit && id) {
      fetchDonor();
    }
  }, [id, isEdit]);

  const fetchDonor = async () => {
    try {
      setLoading(true);
      const response = await donorService.getById(parseInt(id!));
      
      if (response.success) {
        const data = response.data;
        reset({
          order: data.order,
        });
        
        if (data.image_url) {
          setImagePreview(data.image_url);
          setExistingImage(data.image_url);
        }
      } else {
        toast.error('Failed to fetch donor');
        navigate('/admin/donors');
      }
    } catch (error: any) {
      console.error('Fetch error:', error);
      toast.error(error.response?.data?.message || 'Failed to fetch donor');
      navigate('/admin/donors');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg', 'image/gif'];
      if (!validTypes.includes(file.type)) {
        toast.error('Please upload a valid image (JPEG, PNG, WebP, GIF)');
        return;
      }

      if (file.size > 2 * 1024 * 1024) {
        toast.error('Image size should be less than 2MB');
        return;
      }

      setValue('image', file);
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
    }
  };

  const removeImage = () => {
    setValue('image', undefined);
    setImagePreview(existingImage || '');
  };

  const onSubmit = async (data: DonorFormData) => {
    console.log('Form data submitted:', data);
    
    try {
      setLoading(true);
      
      await donorService.initializeCsrf();
      
      const formData = new FormData();
      formData.append('order', data.order.toString());
      
      if (data.image) {
        formData.append('image', data.image);
      } else if (!isEdit) {
        toast.error('Please select an image');
        setLoading(false);
        return;
      }
      
      let response;
      if (isEdit && id) {
        console.log('Updating donor ID:', id);
        response = await donorService.update(parseInt(id), formData);
      } else {
        console.log('Creating new donor');
        response = await donorService.create(formData);
      }
      
      if (response.success) {
        toast.success(`Donor ${isEdit ? 'updated' : 'created'} successfully`);
        navigate('/admin/donors');
      } else {
        toast.error(response.message || 'Operation failed');
      }
    } catch (error: any) {
      console.error('Submit error details:', error);
      
      if (error.response?.status === 419) {
        toast.error('CSRF token expired. Please refresh the page and try again.');
        await donorService.initializeCsrf();
      } else if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        Object.keys(errors).forEach(key => {
          toast.error(`${key}: ${errors[key].join(', ')}`);
        });
      } else {
        toast.error(error.response?.data?.message || 'Something went wrong');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEdit) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <Link
          to="/admin/donors"
          className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-4"
        >
          <ArrowLeft size={18} className="mr-2" />
          Back to Donors
        </Link>
        <h1 className="text-3xl font-bold text-gray-800">
          {isEdit ? 'Edit Donor' : 'Add New Donor'}
        </h1>
        <p className="text-gray-600 mt-2">
          {isEdit ? 'Update donor details' : 'Add a new donor logo'}
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Form Fields */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Donor Details</h2>
              <div className="space-y-4">
                {/* Order */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Display Order
                  </label>
                  <input
                    type="number"
                    min="0"
                    {...register('order', { min: 0, valueAsNumber: true })}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Lower numbers appear first in the carousel
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Image Upload & Actions */}
          <div className="space-y-6">
            {/* Image Upload */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Donor Logo *</h2>
              
              {imagePreview ? (
                <div className="space-y-4">
                  <div className="relative border rounded-lg p-4 bg-gray-50">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full h-40 object-contain"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'https://placehold.co/160x100/2563eb/white?text=Donor';
                      }}
                    />
                    {!existingImage && (
                      <button
                        type="button"
                        onClick={removeImage}
                        className="absolute top-2 right-2 p-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
                      >
                        <X size={18} />
                      </button>
                    )}
                  </div>
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => document.getElementById('image-upload')?.click()}
                      className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                    >
                      Change Logo
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => document.getElementById('image-upload')?.click()}
                  className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors"
                >
                  <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                    <Upload className="text-gray-400" size={24} />
                  </div>
                  <p className="text-gray-600 mb-2">
                    <span className="text-blue-600 font-medium">Click to upload</span> or drag and drop
                  </p>
                  <p className="text-sm text-gray-500">
                    PNG, JPG, GIF, WebP up to 2MB
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    Recommended: 160×100px
                  </p>
                </div>
              )}
              
              <input
                id="image-upload"
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
            </div>

            {/* Actions */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Actions</h2>
              <div className="space-y-3">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all flex items-center justify-center space-x-2 disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <Save size={18} />
                      <span>{isEdit ? 'Update Donor' : 'Add Donor'}</span>
                    </>
                  )}
                </button>
                
                <button
                  type="button"
                  onClick={() => navigate('/admin/donors')}
                  className="w-full px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                
                {isEdit && existingImage && (
                  <a
                    href={existingImage}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center space-x-2"
                  >
                    <Eye size={18} />
                    <span>View Logo</span>
                  </a>
                )}
              </div>
            </div>

            {/* Tips */}
            <div className="bg-blue-50 rounded-xl p-5 border border-blue-200">
              <h3 className="font-semibold text-blue-800 mb-2">Tips</h3>
              <ul className="space-y-2 text-sm text-blue-700">
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 mr-2"></div>
                  Use transparent PNG for best results
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 mr-2"></div>
                  Keep logos under 2MB for fast loading
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 mr-2"></div>
                  Recommended size: 160×100 pixels
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 mr-2"></div>
                  Lower order numbers appear first in carousel
                </li>
              </ul>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default DonorForm;